import React, { useCallback, useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, PieChart, Pie, Cell, Area, AreaChart,
} from "recharts";
import {
  Users, ShoppingBag, Target, TrendingUp, Activity, Search, RefreshCw,
  ShieldCheck, Gauge, Boxes, AlertTriangle, Banknote, Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import adminApi from "../services/adminApi";

/* ---------------- helpers ---------------- */
const faDigits = "۰۱۲۳۴۵۶۷۸۹";
const toFa = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!isFinite(n)) return String(v);
  return Math.round(n).toString().replace(/\d/g, (d) => faDigits[d]);
};
const fmtNum = (n) => (n === null || n === undefined ? "—" : toFa(n));
const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (!isFinite(num)) return String(n);
  if (num >= 1_000_000) return `${toFa((num / 1_000_000).toFixed(1))} م`;
  if (num >= 1000) return `${toFa(Math.round(num / 1000))} هـ`;
  return `${toFa(Math.round(num))}`;
};
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return String(s);
  try {
    return d.toLocaleDateString("fa-IR");
  } catch {
    return s.slice(0, 10);
  }
};
const fmtDay = (s) => (s ? toFa(s.slice(5).replace("-", "/")) : "");

const RANGES = [
  { label: "۷ روز", days: 7 },
  { label: "۳۰ روز", days: 30 },
  { label: "۹۰ روز", days: 90 },
];

const TABS = [
  { key: "dashboard", label: "نمای کلی" },
  { key: "users", label: "کاربران" },
  { key: "products", label: "محصولات و رقبا" },
  { key: "rules", label: "قوانین و توزیع" },
  { key: "activity", label: "فعالیت و سلامت" },
];

const PIE_COLORS = ["#0ea5e9", "#16a34a", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#64748b"];

/* ---------------- building blocks ---------------- */
function Kpi({ icon, label, value, sub, tone = "brand" }) {
  const toneCls = {
    brand: "text-sky-600 bg-sky-50",
    emerald: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    rose: "text-rose-600 bg-rose-50",
    violet: "text-violet-600 bg-violet-50",
  }[tone] || "text-slate-600 bg-slate-50";
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs mb-2">
        <span className={`size-7 rounded-lg flex items-center justify-center ${toneCls}`}>{icon}</span>
        {label}
      </div>
      <div className="text-xl font-bold text-slate-800 leading-tight">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, hint, icon, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-semibold text-slate-700 flex items-center gap-2">
          {icon && <span className="text-sky-500">{icon}</span>}
          {title}
        </h3>
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
        {action}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const cls = {
    emerald: "text-emerald-600", rose: "text-rose-600", slate: "text-slate-700",
    brand: "text-sky-600", amber: "text-amber-600",
  }[tone] || "text-slate-700";
  return (
    <div className="bg-slate-50 rounded-xl py-3 px-2 text-center">
      <div className={`text-lg font-bold ${cls}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

function Empty({ text = "داده‌ای برای نمایش نیست." }) {
  return <p className="text-sm text-slate-400 py-6 text-center">{text}</p>;
}

function Th({ children }) {
  return <th className="font-semibold py-2 px-2 text-right text-[11px] text-slate-400 whitespace-nowrap">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`py-2 px-2 align-top text-sm text-slate-700 ${className}`}>{children}</td>;
}

/* ---------------- main page ---------------- */
export default function AdminPage() {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [d, setD] = useState({}); // کل داده‌ها

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const out = {};
      // همیشه نگاه اجمالی
      const [overview, daily, priceHist, health] = await Promise.all([
        adminApi.overview(days).catch(() => null),
        adminApi.dailyMetrics(days).catch(() => null),
        adminApi.priceHistory(days).catch(() => null),
        adminApi.health().catch(() => null),
      ]);
      out.overview = overview;
      out.daily = daily;
      out.priceHistory = priceHist;
      out.health = health;

      if (tab === "users") {
        out.users = await adminApi.users("", 100).catch(() => []);
      } else if (tab === "products") {
        const [products, competitors] = await Promise.all([
          adminApi.products(days, 100).catch(() => []),
          adminApi.competitors(days, 100).catch(() => []),
        ]);
        out.products = products;
        out.competitors = competitors;
      } else if (tab === "rules") {
        const [rules, dist] = await Promise.all([
          adminApi.rules(100).catch(() => []),
          adminApi.distribution().catch(() => null),
        ]);
        out.rules = rules;
        out.dist = dist;
      } else if (tab === "activity") {
        const [recent, sched] = await Promise.all([
          adminApi.recentActivity(days, 20).catch(() => null),
          adminApi.scheduler(days, 30).catch(() => null),
        ]);
        out.recent = recent;
        out.scheduler = sched;
      }
      setD(out);
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 403 ? "forbidden" : status === 401 ? "unauthorized" : "error");
    } finally {
      setLoading(false);
    }
  }, [days, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const ov = d.overview || {};
  const rangeLabel = RANGES.find((r) => r.days === days)?.label || "";

  if (error === "forbidden") {
    return (
      <div className="max-w-md mx-auto mt-16 text-center bg-white rounded-2xl p-8 border border-slate-200">
        <ShieldCheck className="mx-auto text-amber-500 mb-3" size={36} />
        <p className="text-slate-700 font-medium">این صفحه فقط برای مدیر در دسترس است.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" dir="rtl">
      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/" className="text-xs text-slate-400 hover:text-sky-600 inline-flex items-center gap-1 mb-1">
            ← بازگشت به برنامه
          </Link>
          <h1 className="text-xl font-bold text-slate-800">داشبورد مدیریت</h1>
          <p className="text-sm text-slate-500">تحلیل کاربران، محصولات، رقبا، قوانین قیمت و فعالیت سامانه</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`px-3 py-1.5 rounded-lg text-sm transition ${
                  days === r.days ? "bg-white text-sky-700 font-medium shadow-sm" : "text-slate-500"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            title="بازخوانی"
            className="p-2 rounded-xl bg-slate-100 text-slate-500 hover:text-sky-600 hover:bg-slate-200 transition"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* tabs */}
      <div className="sticky top-0 z-20 -mx-1 px-1 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                tab === t.key ? "bg-sky-600 text-white shadow-sm" : "text-slate-500 hover:bg-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------------- DASHBOARD ---------------- */}
      {tab === "dashboard" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={<Users size={18} />} label={`کاربر کل`} value={fmtNum(ov.users_total)} sub={`${fmtNum(ov.new_users)} ثبت‌نام (${rangeLabel})`} tone="brand" />
            <Kpi icon={<Boxes size={18} />} label="محصول کل" value={fmtNum(ov.products_total)} sub={`${fmtNum(ov.new_products)} جدید (${rangeLabel})`} tone="violet" />
            <Kpi icon={<Target size={18} />} label="رقبا" value={fmtNum(ov.competitors_total)} sub={`${fmtNum(ov.competitors_pending)} در انتظار پردازش`} tone="amber" />
            <Kpi icon={<Activity size={18} />} label="قوانین فعال" value={fmtNum(ov.rules_active)} sub={`${fmtNum(ov.rules_total)} قانون کل`} tone="emerald" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={<Gauge size={18} />} label="تحلیل قیمت کل" value={fmtNum(ov.analyses_total)} sub={`${fmtNum(ov.analyses_window)} تحلیل (${rangeLabel})`} tone="brand" />
            <Kpi icon={<TrendingUp size={18} />} label="نمونهٔ قیمت رقبا" value={fmtNum(ov.price_samples)} sub="تاریخچهٔ ثبت‌شده" tone="violet" />
            <Kpi icon={<Users size={18} />} label="غرفه متصل" value={fmtNum(ov.vendors_connected)} sub={`${fmtNum(ov.users_with_products)} کاربر با محصول`} tone="emerald" />
            <Kpi icon={<Clock size={18} />} label={`اجرای scheduler (${rangeLabel})`} value={fmtNum(ov.scheduler_runs)} sub={`${fmtNum(ov.scheduler_success)} موفق`} tone={ov.scheduler_runs > 0 && ov.scheduler_success < ov.scheduler_runs ? "rose" : "emerald"} />
          </div>

          <Section title="روند روزانه" hint={`ثبت‌نام / محصول / تحلیل / scheduler — ${rangeLabel}`} icon={<TrendingUp size={16} />}>
            {d.daily?.series?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={d.daily.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={20} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }}
                    labelFormatter={(v) => fmtDate(v)}
                    formatter={(v, name) => [toFa(v), dailyLabel(name)]}
                  />
                  <Legend formatter={(v) => dailyLabel(v)} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="new_users" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="new_products" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="analyses" stroke="#16a34a" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="scheduler_runs" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty />
            )}
          </Section>

          <Section title="روند قیمت بازار (رقبا)" hint={`میانگین/کمینه/بیشینه — ${rangeLabel}`} icon={<Banknote size={16} />}>
            {d.priceHistory?.series?.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={d.priceHistory.series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="avgP" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={20} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={fmtMoney} />
                  <Tooltip
                    contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }}
                    labelFormatter={(v) => fmtDate(v)}
                    formatter={(v, name) => [`${toFa(v)} ت`, priceLabel(name)]}
                  />
                  <Legend formatter={(v) => priceLabel(v)} wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="avg_price" name="avg_price" stroke="#0ea5e9" strokeWidth={2} fill="url(#avgP)" dot={false} />
                  <Line type="monotone" dataKey="min_price" stroke="#16a34a" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="max_price" stroke="#ef4444" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty text="هنوز نمونهٔ قیمتی ثبت نشده است." />
            )}
          </Section>
        </>
      )}

      {/* ---------------- USERS ---------------- */}
      {tab === "users" && <UsersTab rows={d.users} onReload={load} />}

      {/* ---------------- PRODUCTS ---------------- */}
      {tab === "products" && (
        <>
          <Section title={`محصولات جدید (${rangeLabel})`} icon={<ShoppingBag size={16} />} hint={d.products ? `${fmtNum(d.products.length)} مورد` : ""}>
            {d.products?.length ? (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <Th>محصول</Th><Th>کاربر / غرفه</Th><Th>دسته</Th><Th>قیمت</Th><Th>رقبا</Th><Th>قانون فعال</Th><Th>تاریخ</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.products.map((p) => (
                      <tr key={p.product_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <Td><div className="font-medium truncate max-w-[180px]">{p.title}</div><div className="text-[10px] text-slate-400">#{p.product_id} · {p.status}</div></Td>
                        <Td><div className="truncate max-w-[120px]">{p.username}</div><div className="text-[10px] text-slate-400">{p.vendor_name || `غرفه ${fmtNum(p.vendor_id)}`}</div></Td>
                        <Td className="text-xs text-slate-500">{p.category_name || "—"}</Td>
                        <Td className="nums-fa text-xs">{fmtMoney(p.price)}</Td>
                        <Td className="nums-fa text-xs">{fmtNum(p.competitors)}</Td>
                        <Td className="nums-fa text-xs">{fmtNum(p.active_rules)}</Td>
                        <Td className="text-[11px] text-slate-400">{fmtDate(p.created_at)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty />}
          </Section>

          <Section title={`رقبای فعال (${rangeLabel})`} icon={<Target size={16} />} hint={d.competitors ? `${fmtNum(d.competitors.length)} مورد` : ""}>
            {d.competitors?.length ? (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <Th>رقیب</Th><Th>فروشنده / شهر</Th><Th>محصول رابط</Th><Th>قیمت</Th><Th>امتیاز</Th><Th>نمونه قیمت</Th><Th>باسلام؟</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.competitors.map((c) => (
                      <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <Td><div className="font-medium truncate max-w-[160px]">{c.name}</div></Td>
                        <Td className="text-xs text-slate-500">{c.vendor_name || "—"}{c.vendor_city ? ` · ${c.vendor_city}` : ""}</Td>
                        <Td className="text-xs text-slate-500 truncate max-w-[120px]">{c.product_title}</Td>
                        <Td className="nums-fa text-xs">{fmtMoney(c.current_price)}</Td>
                        <Td className="nums-fa text-xs">{c.rating_average ? toFa(c.rating_average) : "—"}</Td>
                        <Td className="nums-fa text-xs">{fmtNum(c.price_samples)}</Td>
                        <Td>{c.is_basalam ? <span className="text-emerald-600 text-xs">بله</span> : <span className="text-slate-400 text-xs">خیر</span>}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty />}
          </Section>
        </>
      )}

      {/* ---------------- RULES + DISTRIBUTION ---------------- */}
      {tab === "rules" && (
        <>
          <div className="grid lg:grid-cols-2 gap-5">
            <Section title="توزیع محصول بر دسته" icon={<Boxes size={16} />}>
              {d.dist?.products_by_category?.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={d.dist.products_by_category} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={85} label={(e) => toFa(e.count)}>
                      {d.dist.products_by_category.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => toFa(v)} contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </Section>

            <Section title="توزیع قانون بر نوع مقایسه/هشدار" icon={<Target size={16} />}>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-slate-400 mb-1">نوع مقایسه</div>
                  {d.dist?.rules_by_comparison?.length ? (
                    <ResponsiveContainer width="100%" height={110}>
                      <BarChart data={d.dist.rules_by_comparison} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" hide /><YAxis type="category" dataKey="type" tick={{ fontSize: 12, fill: "#64748b" }} width={70} />
                        <Tooltip formatter={(v) => toFa(v)} contentStyle={{ direction: "rtl", fontSize: 12 }} />
                        <Bar dataKey="count" fill="#0ea5e9" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <Empty />}
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">نوع هشدار</div>
                  {d.dist?.rules_by_alert?.length ? (
                    <ResponsiveContainer width="100%" height={110}>
                      <BarChart data={d.dist.rules_by_alert} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" hide /><YAxis type="category" dataKey="type" tick={{ fontSize: 12, fill: "#64748b" }} width={70} />
                        <Tooltip formatter={(v) => toFa(v)} contentStyle={{ direction: "rtl", fontSize: 12 }} />
                        <Bar dataKey="count" fill="#16a34a" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <Empty />}
                </div>
              </div>
            </Section>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <Section title="برترین کاربران بر اساس تعداد محصول" icon={<Users size={16} />}>
              {d.dist?.top_users_by_products?.length ? (
                <div className="space-y-1">
                  {d.dist.top_users_by_products.map((u) => (
                    <div key={u.user_id} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                      <span className="truncate">{u.username}{u.vendor_name ? <span className="text-slate-400 text-xs"> · {u.vendor_name}</span> : null}</span>
                      <span className="text-slate-600 font-medium shrink-0">{fmtNum(u.products)} محصول</span>
                    </div>
                  ))}
                </div>
              ) : <Empty />}
            </Section>
            <Section title="برترین کاربران بر اساس تعداد رقیب" icon={<Target size={16} />}>
              {d.dist?.top_users_by_competitors?.length ? (
                <div className="space-y-1">
                  {d.dist.top_users_by_competitors.map((u) => (
                    <div key={u.user_id} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                      <span className="truncate">{u.username}{u.vendor_name ? <span className="text-slate-400 text-xs"> · {u.vendor_name}</span> : null}</span>
                      <span className="text-slate-600 font-medium shrink-0">{fmtNum(u.competitors)} رقیب</span>
                    </div>
                  ))}
                </div>
              ) : <Empty />}
            </Section>
          </div>

          <Section title="قوانین قیمت‌گذاری" icon={<Activity size={16} />} hint={d.rules ? `همه زمان‌ها · ${fmtNum(d.rules.length)} مورد` : ""}>
            {d.rules?.length ? (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <Th>نام قانون</Th><Th>محصول</Th><Th>کاربر</Th><Th>نوع</Th><Th>مقایسه</Th><Th>آستانه</Th><Th>فعال</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rules.map((r) => (
                      <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <Td><div className="font-medium truncate max-w-[140px]">{r.name}</div></Td>
                        <Td className="text-xs text-slate-500 truncate max-w-[120px]">{r.product_title}</Td>
                        <Td className="text-xs text-slate-500">{r.username}</Td>
                        <Td className="text-xs">{ruleFa(r.rule_type)}</Td>
                        <Td className="text-xs">{compareFa(r.comparison_type)}</Td>
                        <Td className="nums-fa text-xs">{toFa(r.threshold)}</Td>
                        <Td>{r.is_active ? <span className="text-emerald-600 text-xs">فعال</span> : <span className="text-slate-400 text-xs">غیرفعال</span>}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty />}
          </Section>
        </>
      )}

      {/* ---------------- ACTIVITY ---------------- */}
      {tab === "activity" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="وضعیت API" value={d.health?.status === "ok" ? "سالم" : "—"} tone={d.health?.status === "ok" ? "emerald" : "rose"} />
            <Stat label="وضعیت دیتابیس" value={d.health?.database === "ok" ? "سالم" : "—"} tone={d.health?.database === "ok" ? "emerald" : "rose"} />
            <Stat label={`اجرای موفق (${rangeLabel})`} value={fmtNum(d.scheduler?.stats?.success)} tone="emerald" />
            <Stat label={`اجرای ناموفق (${rangeLabel})`} value={fmtNum(d.scheduler?.stats?.failed)} tone={d.scheduler?.stats?.failed > 0 ? "rose" : "slate"} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <Section title={`تحلیل‌های قیمت (${rangeLabel})`} icon={<Gauge size={16} />} hint={d.recent?.analyses ? `${fmtNum(d.recent.analyses.length)} مورد` : ""}>
              {d.recent?.analyses?.length ? (
                <div className="space-y-2">
                  {d.recent.analyses.map((a) => (
                    <div key={a.id} className="rounded-xl border border-slate-100 bg-slate-50 p-2.5">
                      <div className="flex justify-between items-center gap-2">
                        <span className="font-medium text-sm truncate">{a.product_title}</span>
                        <span className="text-[10px] font-medium rounded-full px-2 py-0.5 shrink-0 text-sky-600 bg-sky-50">
                          {fmtNum(a.competitor_count)} رقیب
                        </span>
                      </div>
                      <div className="flex justify-between mt-1 text-[11px] text-slate-400 nums-fa">
                        <span>قیمت ما: {fmtMoney(a.our_price)}</span>
                        <span>{fmtDate(a.analysis_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <Empty />}
            </Section>

            <Section title={`ثبت‌نام‌ها (${rangeLabel})`} icon={<Users size={16} />} hint={d.recent?.signups ? `${fmtNum(d.recent.signups.length)} مورد` : ""}>
              {d.recent?.signups?.length ? (
                <div className="space-y-1">
                  {d.recent.signups.map((s) => (
                    <div key={s.id} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                      <span className="truncate">{s.username || s.email || `کاربر ${s.id}`}</span>
                      <span className="text-[11px] text-slate-400">{fmtDate(s.created_at)}</span>
                    </div>
                  ))}
                </div>
              ) : <Empty />}
            </Section>
          </div>

          <Section title="لاگ‌های پردازش پس‌زمینه (scheduler)" icon={<Clock size={16} />} hint={d.scheduler?.items ? `${fmtNum(d.scheduler.items.length)} مورد` : ""}>
            {d.scheduler?.items?.length ? (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <Th>وظیفه</Th><Th>وضعیت</Th><Th>زمان</Th><Th>جزئیات</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.scheduler.items.map((s) => (
                      <tr key={s.id} className="border-b border-slate-50">
                        <Td className="text-xs font-medium">{s.task_name}</Td>
                        <Td>
                          <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${s.status === "success" ? "text-emerald-600 bg-emerald-50" : "text-rose-600 bg-rose-50"}`}>
                            {s.status}
                          </span>
                        </Td>
                        <Td className="text-[11px] text-slate-400">{fmtDate(s.execution_time)}</Td>
                        <Td className="text-[11px] text-slate-400 truncate max-w-[260px]">{s.details || "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty text="هنوز لاگی ثبت نشده است." />}
          </Section>
        </>
      )}
    </div>
  );
}

/* ---------------- users tab (with drill-down) ---------------- */
function UsersTab({ rows, onReload }) {
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [submitted, setSubmitted] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    setSubmitted(q.trim());
    try {
      setDetailLoading(true);
      setDetailError(null);
      const res = await adminApi.users(q.trim(), 100);
      // اگر یک نتیجه دقیق بود، جزئیاتش را باز کن
      if (q.trim() && res.length === 1) {
        openUser(res[0].id);
      } else {
        setDetail(null);
      }
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openUser = async (id) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      setDetail(await adminApi.user(id));
    } catch (err) {
      setDetailError(err?.response?.status === 404 ? "کاربر یافت نشد" : "خطا در دریافت جزئیات");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const shown = submitted ? rows : rows;

  return (
    <>
      <Section title="کاربران" icon={<Users size={16} />} action={
        <form onSubmit={search} className="flex gap-2">
          <div className="relative">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="id / نام کاربری / غرفه"
              className="bg-slate-50 border border-slate-200 rounded-xl pr-8 pl-3 py-1.5 text-sm focus:outline-none focus:border-sky-400 w-52"
            />
          </div>
          <button type="submit" className="px-3 py-1.5 bg-sky-600 text-white rounded-xl text-sm hover:bg-sky-700">جستجو</button>
        </form>
      }>
        {shown?.length ? (
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <Th>کاربر</Th><Th>غرفه</Th><Th>محصول</Th><Th>رقبا</Th><Th>قوانین فعال</Th><Th>تحلیل</Th><Th>ادمین</Th><Th>ثبت‌نام</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((u) => (
                  <tr key={u.id} onClick={() => openUser(u.id)} className="border-b border-slate-50 hover:bg-sky-50/40 cursor-pointer">
                    <Td><div className="font-medium">#{fmtNum(u.id)}</div><div className="text-[10px] text-slate-400 truncate max-w-[120px]">{u.username}</div></Td>
                    <Td className="text-xs text-slate-500 truncate max-w-[120px]">{u.vendor_name || (u.vendor_id ? `غرفه ${fmtNum(u.vendor_id)}` : "—")}</Td>
                    <Td className="nums-fa text-xs">{fmtNum(u.products)}</Td>
                    <Td className="nums-fa text-xs">{fmtNum(u.competitors)}</Td>
                    <Td className="nums-fa text-xs">{fmtNum(u.active_rules)}</Td>
                    <Td className="nums-fa text-xs">{fmtNum(u.analyses)}</Td>
                    <Td>{u.is_admin ? <span className="text-amber-600 text-xs">بله</span> : <span className="text-slate-300 text-xs">—</span>}</Td>
                    <Td className="text-[11px] text-slate-400">{fmtDate(u.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty />}
      </Section>

      {(detail || detailLoading || detailError) && (
        <Section title="جزئیات کاربر" icon={<Activity size={16} />} action={
          <button onClick={() => { setDetail(null); setDetailError(null); }} className="text-xs text-slate-400 hover:text-slate-600">بستن ✕</button>
        }>
          {detailLoading && <p className="text-sm text-slate-400">در حال بارگذاری…</p>}
          {detailError && <p className="text-sm text-rose-500">{detailError}</p>}
          {detail && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="محصول" value={fmtNum(detail.counts.products)} tone="brand" />
                <Stat label="رقبا" value={fmtNum(detail.counts.competitors)} tone="violet" />
                <Stat label="قوانین فعال" value={fmtNum(detail.counts.active_rules)} tone="emerald" />
                <Stat label="تحلیل قیمت" value={fmtNum(detail.counts.analyses)} tone="amber" />
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-sm grid sm:grid-cols-2 gap-y-1.5">
                <KV k="کاربر" v={`#${fmtNum(detail.user.id)} · ${detail.user.username || "—"}`} />
                <KV k="ایمیل/موبایل" v={detail.user.email || "—"} />
                <KV k="غرفه" v={detail.user.vendor_name || (detail.user.vendor_id ? `#${fmtNum(detail.user.vendor_id)}` : "بدون غرفه")} />
                <KV k="ثبت‌نام" v={fmtDate(detail.user.created_at)} />
                <KV k="آخرین محصول" v={fmtDate(detail.last_product)} />
                <KV k="آخرین تحلیل" v={fmtDate(detail.last_analysis)} />
              </div>
              {detail.products?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-600 mb-2">محصولات این کاربر (۲۰ مورد اخیر)</h4>
                  <div className="space-y-1">
                    {detail.products.map((p) => (
                      <div key={p.product_id} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                        <span className="truncate">{p.title}</span>
                        <span className="text-xs text-slate-400 shrink-0 nums-fa">{fmtMoney(p.price)} · {fmtNum(p.competitors)} رقیب</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>
      )}
    </>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-400">{k}</span>
      <span className="text-slate-700 font-medium text-left truncate">{v}</span>
    </div>
  );
}

/* ---------------- label helpers ---------------- */
function dailyLabel(key) {
  return { new_users: "ثبت‌نام", new_products: "محصول جدید", analyses: "تحلیل قیمت", scheduler_runs: "اجرای scheduler" }[key] || key;
}
function priceLabel(key) {
  return { avg_price: "میانگین", min_price: "کمینه", max_price: "بیشینه" }[key] || key;
}
function ruleFa(t) {
  return { percentage: "درصدی", fixed: "ثابت" }[t] || t;
}
function compareFa(t) {
  return { average: "میانگین", min: "کمترین", max: "بیشترین" }[t] || t;
}
